import type { Query, QueryClient } from '@tanstack/react-query'

// Whole-cache, because hand-maintained per-mutation invalidation lists rot silently: the key you forgot shows
// stale data and nothing fails.
// ACTIVE queries invalidate — refetch while KEEPING data, so the current screen never falls back to its skeleton.
// INACTIVE ones reset, so the next visit suspends into its pendingComponent instead of painting the stale value first.
// An in-flight inactive query is a loader's fire-and-forget prefetch, and resetQueries would ABORT it, so those are
// left to land and invalidated once they settle.
export function revalidateAfterWrite(client: QueryClient, predicate?: (query: Query) => boolean): Promise<void> {
  const refetched = client.invalidateQueries({ type: 'active', predicate })
  void client.resetQueries({ type: 'inactive', fetchStatus: 'idle', predicate })

  // Not `fetching`: that misses a request PAUSED offline, equally mid-flight and equally not to cancel.
  const inFlight = client
    .getQueryCache()
    .findAll({ type: 'inactive', predicate })
    .filter((query) => query.state.fetchStatus !== 'idle')

  if (inFlight.length > 0) {
    // `query.promise` is undefined for a query that never fetched; impossible here, but the type allows it.
    void Promise.allSettled(inFlight.map((query) => query.promise ?? Promise.resolve())).then(() => {
      for (const query of inFlight) void client.invalidateQueries({ queryKey: query.queryKey, exact: true })
    })
  }

  return refetched
}
